import {
    selectedParametersListItemRemoveTwClass,
    selectedParametersListItemTwClass,
    selectedParametersListTwClass,
    selectedParametersNameTwClass,
} from './SelectedFilters';
import { getEmptyDefaultProductFiltersMap } from 'config/constants';
import { ProductFilterOptionsFragmentApi } from 'graphql/generated';
import { formatParameterValue } from 'helpers/formaters/formatFilter';
import { useUpdateFilterQuery } from 'helpers/queryParams/useUpdateFilterQuery';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { RemoveThinIcon } from 'public/svg/IconsSvg';
import { Fragment, memo } from 'react';
import { FilterOptionsParameterUrlQueryType } from 'types/productFilter';

export type SelectedFiltersParameterProps = {
    selectedParameter: FilterOptionsParameterUrlQueryType;
    filterOptions: ProductFilterOptionsFragmentApi;
};

export const SelectedFiltersParameter: FC<SelectedFiltersParameterProps> = memo(
    ({ selectedParameter, filterOptions }) => {
        const t = useTypedTranslationFunction();

        const selectedParameterOptions = filterOptions.parameters?.find(
            (parameterOption) => parameterOption.uuid === selectedParameter.parameter,
        );

        const { updateFilterParametersQuery } = useUpdateFilterQuery();

        const isSliderParameter = selectedParameterOptions?.__typename === 'ParameterSliderFilterOption';
        const isColorParameter = selectedParameterOptions?.__typename === 'ParameterColorFilterOption';
        const isCheckBoxParameter = selectedParameterOptions?.__typename === 'ParameterCheckboxFilterOption';
        const isLogicalType =
            selectedParameterOptions && 'isLogicalType' in selectedParameterOptions
                ? selectedParameterOptions.isLogicalType
                : false;

        const defaultProductFiltersMap = getEmptyDefaultProductFiltersMap();
        const selectedParameterValues =
            // hack typescript because it is confused about filtering shared types
            isCheckBoxParameter || isColorParameter
                ? (
                      selectedParameterOptions.values as {
                          uuid: string;
                          text: string;
                          isSelected: boolean;
                      }[]
                  ).filter((selectedParameterValue) => {
                      return (
                          selectedParameter.values?.includes(selectedParameterValue.uuid) ||
                          defaultProductFiltersMap.parameters
                              .get(selectedParameter.parameter)
                              ?.has(selectedParameterValue.uuid)
                      );
                  })
                : undefined;

        return (
            <>
                <Fragment key={selectedParameterOptions?.uuid}>
                    {isSliderParameter ? (
                        <ul className={selectedParametersListTwClass}>
                            <p className={selectedParametersNameTwClass}>{selectedParameterOptions.name}</p>
                            <li className={selectedParametersListItemTwClass}>
                                {selectedParameter.minimalValue && (
                                    <>
                                        <span>{t('from')}&nbsp;</span>
                                        {selectedParameter.minimalValue || selectedParameterOptions.minimalValue}
                                        {!!selectedParameterOptions.unit?.name &&
                                            `\xa0${selectedParameterOptions.unit.name}`}
                                    </>
                                )}

                                {selectedParameter.maximalValue && (
                                    <>
                                        <span>&nbsp;{t('to')}&nbsp;</span>
                                        {selectedParameter.maximalValue || selectedParameterOptions.maximalValue}
                                        {selectedParameterOptions.unit?.name &&
                                            `\xa0${selectedParameterOptions.unit.name}`}
                                    </>
                                )}

                                <RemoveThinIcon
                                    className={selectedParametersListItemRemoveTwClass}
                                    onClick={() =>
                                        updateFilterParametersQuery(selectedParameterOptions.uuid, undefined)
                                    }
                                />
                            </li>
                        </ul>
                    ) : (
                        <ul className={selectedParametersListTwClass}>
                            <p className={selectedParametersNameTwClass}>{selectedParameterOptions?.name}</p>
                            {selectedParameterValues?.map((selectedValue) => (
                                <li className={selectedParametersListItemTwClass} key={selectedValue.uuid}>
                                    {formatParameterValue(selectedValue.text, isLogicalType, t)}
                                    <RemoveThinIcon
                                        className={selectedParametersListItemRemoveTwClass}
                                        onClick={() =>
                                            updateFilterParametersQuery(selectedParameter.parameter, selectedValue.uuid)
                                        }
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </Fragment>
            </>
        );
    },
);

SelectedFiltersParameter.displayName = 'Parameters';
